import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, HouseholdRosterRow, WorkspaceInviteRow } from './schema'
import type { WorkspaceRole } from '@/types/workspace'

/**
 * v0.8.51 — Household sharing Phase 2 cloud calls.
 *
 * Household membership is cloud-only (a local/anonymous build has nobody to
 * share with), so unlike debts/ledger there's no localStorage twin and no
 * repository interface — these are thin wrappers over the migration-004
 * RPCs. Every RPC re-checks the caller's role server-side; nothing here is
 * a security boundary.
 */

export interface HouseholdMember {
  userId: string
  email: string
  displayName: string | null
  role: WorkspaceRole
  joinedAt: string | null
}

export interface PendingInvite {
  id: string
  email: string
  createdAt: string
  expiresAt: string
}

type Client = SupabaseClient<Database>

/** Raised when migration 004 hasn't been applied to the cloud project. */
export const HOUSEHOLD_MIGRATION_MISSING =
  'Household sharing needs a database update. Apply supabase/migrations/20261031_004_household_invites.sql in the Supabase SQL editor.'

/**
 * PostgREST answers PGRST202 ("Could not find the function …") when an RPC
 * doesn't exist and 42P01 when a table doesn't. Both mean "migration 004
 * isn't applied" — surface that instead of a schema-cache message. Errors
 * raised by our own functions carry user-facing copy and pass through.
 */
export function householdErrorMessage(error: { message: string; code?: string }): string {
  if (
    error.code === 'PGRST202' ||
    error.code === '42P01' ||
    /could not find the function|schema cache|does not exist/i.test(error.message)
  ) {
    return HOUSEHOLD_MIGRATION_MISSING
  }
  return error.message
}

function fail(error: { message: string; code?: string }): never {
  throw new Error(householdErrorMessage(error))
}

export async function fetchRoster(client: Client, workspaceId: string): Promise<HouseholdMember[]> {
  const { data, error } = await client.rpc('get_household_roster', { ws: workspaceId })
  if (error) fail(error)
  return ((data ?? []) as HouseholdRosterRow[]).map((r) => ({
    userId: r.user_id,
    email: r.email,
    displayName: r.display_name,
    role: r.role,
    joinedAt: r.joined_at,
  }))
}

/** Owner-only (RLS). Returns pending invites — not accepted, revoked or expired. */
export async function fetchPendingInvites(
  client: Client,
  workspaceId: string,
  now: Date = new Date()
): Promise<PendingInvite[]> {
  const { data, error } = await client
    .from('workspace_invites')
    .select('id, email, created_at, expires_at, accepted_at, revoked_at')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })
  if (error) fail(error)
  return (
    (data ?? []) as Pick<
      WorkspaceInviteRow,
      'id' | 'email' | 'created_at' | 'expires_at' | 'accepted_at' | 'revoked_at'
    >[]
  )
    .filter((r) => !r.accepted_at && !r.revoked_at && new Date(r.expires_at) > now)
    .map((r) => ({ id: r.id, email: r.email, createdAt: r.created_at, expiresAt: r.expires_at }))
}

/** Returns the plaintext invite token — the only time it exists outside the link. */
export async function createInvite(
  client: Client,
  workspaceId: string,
  email: string
): Promise<string> {
  const { data, error } = await client.rpc('create_workspace_invite', {
    ws: workspaceId,
    invitee_email: email,
  })
  if (error) fail(error)
  if (typeof data !== 'string' || data.length === 0) throw new Error('Invite was not created')
  return data
}

export async function revokeInvite(client: Client, inviteId: string): Promise<void> {
  const { error } = await client.rpc('revoke_workspace_invite', { invite_id: inviteId })
  if (error) fail(error)
}

/** Returns the joined workspace id. Idempotent for the user who already accepted. */
export async function acceptInvite(client: Client, token: string): Promise<string> {
  const { data, error } = await client.rpc('accept_workspace_invite', { token })
  if (error) fail(error)
  if (typeof data !== 'string' || data.length === 0) throw new Error('Invite could not be accepted')
  return data
}

/** Owner removes someone else, or a non-owner leaves (delete policy enforces which). */
export async function removeMember(
  client: Client,
  workspaceId: string,
  userId: string
): Promise<void> {
  const { error } = await client
    .from('workspace_members')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
  if (error) fail(error)
}

export async function setDisplayName(
  client: Client,
  workspaceId: string,
  name: string
): Promise<void> {
  const { error } = await client.rpc('set_my_display_name', { ws: workspaceId, name })
  if (error) fail(error)
}
